import { useEffect } from 'react';

export type KeyboardShortcutMap = Map<string, () => void>;

type Options = {
  enabled: boolean;
  // Fires on Escape ahead of (and regardless of) the typing-field guard below — Escape
  // always means "dismiss", even while an input inside the same menu has focus.
  onEscape?: () => void;
};

// Dispatches single-key and mod+key ("mod" being Cmd on Mac, Ctrl elsewhere) chords to the
// given shortcut map while `enabled`. Ignores keydowns aimed at an editable field (an input,
// textarea, select, or contenteditable element) so typing there isn't hijacked, and excludes
// Shift from the modifier chord so browser chords like Ctrl+Shift+C are left alone.
export function useKeyboardShortcuts(shortcuts: KeyboardShortcutMap, { enabled, onEscape }: Options) {
  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) return;

      if (e.key === 'Escape') {
        if (!onEscape) return;

        e.preventDefault();
        onEscape();
        return;
      }

      const target = e.target as Element | null;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"]')) return;

      const chord = (e.metaKey || e.ctrlKey) && !e.shiftKey;
      const key = e.key.toLowerCase();

      const action = shortcuts.get(chord ? `mod+${key}` : key);
      if (!action) return;

      e.preventDefault();
      action();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled, onEscape]);
}
